import {
  type Genql,
  type GenqlResult,
} from '@bizzdesign/bizzdesign-sdk-type-genql';
import {
  type GetSkillsParameters,
  type Skill,
  type SkillSearchResult,
} from '../interfaces';

/** One page of a skills listing, before the client turns the pages into a generator. */
export type SkillsPage = {
  skills: Skill[];
  totalCount: number | undefined;
};

/** The list and search reads omit `instructions` and `sharedWith` from their selection. */
type MappableSkill = Omit<Genql.Skill, 'instructions' | 'sharedWith'> & {
  instructions?: string | null;
  sharedWith?: string[] | null;
};

export function mapSkill(skillOutput: GenqlResult<MappableSkill>): Skill {
  return {
    id: skillOutput.id,
    trigger: skillOutput.trigger ?? undefined,
    createdBy: skillOutput.createdBy,
    type: skillOutput.type,
    name: skillOutput.name,
    description: skillOutput.description ?? undefined,
    instructions: skillOutput.instructions ?? undefined,
    scope: skillOutput.scope,
    enabled: skillOutput.enabled,
    sharedWith: skillOutput.sharedWith ?? [],
    createdAt: skillOutput.createdAt,
    updatedAt: skillOutput.updatedAt,
    updatedBy: skillOutput.updatedBy ?? undefined,
  };
}

export function mapSkillsPage(
  pageOutput: GenqlResult<Pick<Genql.SkillPage, 'totalCount'>> & {
    skills: GenqlResult<MappableSkill>[];
  },
): SkillsPage {
  return {
    skills: pageOutput.skills.map(mapSkill),
    totalCount: pageOutput.totalCount ?? undefined,
  };
}

export function mapSkillSearchResult(
  searchOutput: GenqlResult<Omit<Genql.SkillSearchResult, 'skill'>> & {
    skill: GenqlResult<MappableSkill>;
  },
): SkillSearchResult {
  return {
    score: searchOutput.score,
    skill: mapSkill(searchOutput.skill),
  };
}

/**
 * Turns the single-field sort option into the backend's `SkillSort` input, which accepts at
 * most one field.
 */
export function mapSkillSort(
  sort: GetSkillsParameters['sort'],
): Genql.SkillSort | undefined {
  if (!sort) return undefined;
  return { [sort.field]: sort.order };
}
